import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { 
  FileText, Plus, Search, MoreHorizontal, Pencil, Trash2, Upload, 
  Type, PenTool, Calendar, CheckSquare, ChevronDown, Hash, AlignLeft,
  Eye, Copy, FileUp, GripVertical, X, Save, Maximize2,
  Heading1, ListChecks, Image as ImageIcon, Database, Radio
} from 'lucide-react';
import { toast } from 'sonner';

// Lazy load FormDesigner to avoid issues with PDF.js
import { lazy, Suspense } from 'react';
const FormDesigner = lazy(() => import('@/components/forms/FormDesigner'));
import { RELATED_FIELD_GROUPS } from '@/components/forms/relatedFieldGroups';

const FIELD_TYPES = [
  { type: 'heading',     label: 'H1 Section Label',  icon: Heading1,    color: 'bg-slate-100 text-slate-700' },
  { type: 'text',        label: 'Text Field',         icon: Type,        color: 'bg-blue-100 text-blue-600' },
  { type: 'textarea',    label: 'Text Area',          icon: AlignLeft,   color: 'bg-indigo-100 text-indigo-600' },
  { type: 'dropdown',    label: 'Dropdown',           icon: ChevronDown, color: 'bg-cyan-100 text-cyan-600' },
  { type: 'multiselect', label: 'Multi-select',       icon: ListChecks,  color: 'bg-teal-100 text-teal-700' },
  { type: 'quick_select',label: 'Quick Select',       icon: Radio,       color: 'bg-fuchsia-100 text-fuchsia-700' },
  { type: 'checkbox',    label: 'Checkbox',           icon: CheckSquare, color: 'bg-amber-100 text-amber-600' },
  { type: 'date',        label: 'Date',               icon: Calendar,    color: 'bg-green-100 text-green-600' },
  { type: 'number',      label: 'Number',             icon: Hash,        color: 'bg-rose-100 text-rose-600' },
  { type: 'image_upload',label: 'Image/Video Upload', icon: ImageIcon,   color: 'bg-orange-100 text-orange-700' },
  { type: 'signature',   label: 'Signature Box',      icon: PenTool,     color: 'bg-purple-100 text-purple-600' },
  { type: 'related',     label: 'Related Field Value',icon: Database,    color: 'bg-emerald-100 text-emerald-700' },
];

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'permit', label: 'Permit Application' },
  { value: 'estimate', label: 'Estimate' },
  { value: 'job', label: 'Job' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'credit_card', label: 'Credit Card Authorization' },
];

export default function CustomFormsPage() {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [draggedId, setDraggedId] = useState(null);
  const canReorder = search === '' && categoryFilter === 'all';
  
  // TEMA C1 (Alian, video "Problema al subir un PDF nuevo"): persistent,
  // readable error banner in the PDF tab — not just a toast that disappears.
  const [pdfUploadError, setPdfUploadError] = useState(null);
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  
  // Form state
  const [form, setForm] = useState({
    name: '',
    description: '',
    category: 'general',
    fields: [],
    pdf_data: null,
    is_active: true,
    applicable_counties: [],
    official_role: ''
  });
  const [counties, setCounties] = useState([]);
  
  // PDF viewer state
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const pdfContainerRef = useRef(null);
  const [fullscreenDesigner, setFullscreenDesigner] = useState(false);
  
  const fetchTemplates = useCallback(async () => {
    try {
      const res = await api.get('/custom-forms/templates');
      setTemplates(res.data);
    } catch (err) {
      toast.error(t('errors.loadingError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Load operational counties (for the county multi-select)
  useEffect(() => {
    api.get('/settings').then(res => {
      const list = (res.data && res.data.counties) || [];
      setCounties(Array.isArray(list) ? list.map(c => (typeof c === 'string' ? c : c.name)).filter(Boolean) : []);
    }).catch(() => setCounties([]));
  }, []);

  const filteredTemplates = templates.filter(tpl => {
    // Internal "structured" placeholders (Permit Application / Completion Report
    // routing markers, 0 fields, never editable) are never shown here — they
    // looked like confusing duplicates of the real official PDF template.
    // Their status/role lives in the OFICIAL badge on the real template row.
    if (tpl.is_structured) return false;
    const matchesSearch = tpl.name.toLowerCase().includes(search.toLowerCase()) ||
                         tpl.description?.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || tpl.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: '',
      description: '',
      category: 'general',
      fields: [],
      pdf_data: null,
      is_active: true,
      applicable_counties: [],
      official_role: ''
    });
    setPdfPreviewUrl(null);
    setPdfUploadError(null);
    setActiveTab('details');
    setDialogOpen(true);
  };

  const openEdit = (template) => {
    setEditing(template);
    setForm({
      name: template.name,
      description: template.description || '',
      category: template.category,
      fields: template.fields || [],
      pdf_data: null, // Don't load existing PDF data
      is_active: template.is_active,
      applicable_counties: template.applicable_counties || [],
      official_role: template.official_role || ''
    });
    // Prefix with backend URL so <iframe>, react-pdf and all embeds resolve
    // correctly (relative /api paths fail in some mobile/embedded browsers).
    const absUrl = template.pdf_url
      ? (template.pdf_url.startsWith('http')
          ? template.pdf_url
          : `${process.env.REACT_APP_BACKEND_URL}${template.pdf_url}`)
      : null;
    setPdfPreviewUrl(absUrl);
    setPdfUploadError(null);
    setActiveTab('details');
    setDialogOpen(true);
  };

  const handleAutoMapCuttings = async () => {
    if (!editing?.id) {
      toast.error('Guarda la plantilla primero para poder auto-mapear.');
      return;
    }
    try {
      const res = await api.post(`/custom-forms/templates/${editing.id}/automap-cuttings`);
      setForm(prev => ({ ...prev, fields: res.data.fields || prev.fields }));
      toast.success(res.data.added
        ? 'Cutting Log, mapa y Permit No. mapeados automáticamente'
        : 'Esta plantilla ya tenía el Cutting Log mapeado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo auto-mapear el Cutting Log');
    }
  };

  const handlePdfUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPdfUploadError(null);

    if (file.size > 20 * 1024 * 1024) { // 20MB limit (matches backend)
      setPdfUploadError(t('customForms.pdfTooLarge'));
      e.target.value = '';
      return;
    }

    // TEMA C1 (Alian, "Problema al subir un PDF nuevo"): file.type/extension
    // is what the OS/browser THINKS the file is — a Word doc renamed or
    // "printed" incorrectly to .pdf still reports application/pdf. Read the
    // real magic bytes ("%PDF-") client-side so he gets the clear message
    // immediately, without a round-trip to the backend.
    const sigReader = new FileReader();
    sigReader.onload = (event) => {
      const buf = new Uint8Array(event.target.result);
      const header = String.fromCharCode(...buf.slice(0, 5));
      if (header !== '%PDF-') {
        setPdfUploadError(t('customForms.notAPdfFile'));
        e.target.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev2) => {
        const base64 = ev2.target?.result;
        setForm({ ...form, pdf_data: base64 });
        setPdfPreviewUrl(base64);
        toast.success(t('customForms.pdfUploaded'));
      };
      reader.readAsDataURL(file);
    };
    sigReader.readAsArrayBuffer(file.slice(0, 5));
  };

  const addField = (type) => {
    const def = FIELD_TYPES.find(f => f.type === type);
    const newField = {
      id: `field_${Date.now()}`,
      type: type,
      label: `${def?.label || 'Field'} ${form.fields.length + 1}`,
      x: 10,
      y: 10 + (form.fields.length * 8) % 80,
      width: type === 'signature' ? 25 : (type === 'textarea' ? 30 : (type === 'heading' ? 60 : 30)),
      height: type === 'signature' ? 10 : (type === 'textarea' ? 15 : (type === 'heading' ? 6 : 5)),
      page: 1,
      required: false,
      default_value: '',
      options: (type === 'dropdown' || type === 'multiselect' || type === 'quick_select') ? ['Option 1', 'Option 2'] : [],
      text_align: 'left',
      label_font_size: 14,
      content_font_size: 12,
      remove_border: false,
      internal_only: false,
      mapped_field: '',
      condition: null,
    };
    setForm({ ...form, fields: [...form.fields, newField] });
    setSelectedField(newField.id);
  };

  const updateField = (fieldId, updates) => {
    setForm({
      ...form,
      fields: form.fields.map(f => f.id === fieldId ? { ...f, ...updates } : f)
    });
  };

  const removeField = (fieldId) => {
    setForm({
      ...form,
      fields: form.fields.filter(f => f.id !== fieldId)
    });
    if (selectedField === fieldId) {
      setSelectedField(null);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error(t('errors.requiredField'));
      return;
    }
    
    try {
      let saved;
      if (editing) {
        const payload = {
          name: form.name,
          description: form.description,
          category: form.category,
          fields: form.fields,
          is_active: form.is_active,
          applicable_counties: form.applicable_counties || [],
          official_role: form.official_role || '',
        };
        // Only send pdf_data if the user uploaded a new PDF in this edit session
        if (form.pdf_data) payload.pdf_data = form.pdf_data;
        const res = await api.put(`/custom-forms/templates/${editing.id}`, payload);
        saved = res.data;
        toast.success(t('success.updated'));
      } else {
        const res = await api.post('/custom-forms/templates', form);
        saved = res.data;
        toast.success(t('success.created'));
      }
      setPdfUploadError(null);
      // Save keeps the designer open (Alian: "no quiero estar abriendo y
      // cerrando... quiero dejar la plantilla abierta"). Switch a brand-new
      // template into edit mode so a second Save does PUT, not another POST.
      setEditing(saved);
      fetchTemplates();
    } catch (err) {
      const detail = err.response?.data?.detail || t('errors.savingError');
      toast.error(detail);
      // If the backend rejected the PDF itself, show it as a persistent
      // banner in the PDF tab too (Alian: "mostrar el error tal cual, no un
      // toast generico que se va").
      if (typeof detail === 'string' && /pdf/i.test(detail)) {
        setPdfUploadError(detail);
        setActiveTab('pdf');
      }
    }
  };

  const handleDelete = async (templateId) => {
    if (!confirm(t('customForms.confirmDelete'))) return;
    
    try {
      await api.delete(`/custom-forms/templates/${templateId}`);
      toast.success(t('success.deleted'));
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.deletingError'));
    }
  };

  const duplicateTemplate = async (template) => {
    try {
      await api.post('/custom-forms/templates', {
        name: `${template.name} (Copy)`,
        description: template.description,
        category: template.category,
        fields: template.fields,
        is_active: false
      });
      toast.success(t('customForms.duplicated'));
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.generic'));
    }
  };

  const getFieldTypeInfo = (type) => FIELD_TYPES.find(f => f.type === type) || FIELD_TYPES[0];
  const getCategoryLabel = (cat) => CATEGORIES.find(c => c.value === cat)?.label || cat;

  // Drag & drop reordering — Alian: "necesito moverlo, agarrarlo con la
  // manito y subirlo o bajarlo... en el orden que yo ponga estas plantillas".
  // This order is persisted via sort_order and reused everywhere (battery,
  // designer, pickers). Reordering is disabled while a search/filter is
  // active since the visual order wouldn't match the real underlying order.
  const handleDragStart = (e, id) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = async (e, targetId) => {
    e.preventDefault();
    if (!draggedId || draggedId === targetId) { setDraggedId(null); return; }
    const list = [...templates];
    const fromIdx = list.findIndex(t => t.id === draggedId);
    const toIdx = list.findIndex(t => t.id === targetId);
    setDraggedId(null);
    if (fromIdx === -1 || toIdx === -1) return;
    const [moved] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, moved);
    setTemplates(list);
    try {
      await api.put('/custom-forms/templates/reorder', { ordered_ids: list.map(t => t.id) });
    } catch (err) {
      toast.error(t('errors.savingError'));
      fetchTemplates();
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-3 border-[#003366]/20 border-t-[#003366] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="custom-forms-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight flex items-center gap-2">
            <FileText className="w-6 h-6 text-[#3399FF]" />
            {t('customForms.title')}
          </h1>
          <p className="text-sm text-gray-500 mt-1">{t('customForms.subtitle')}</p>
        </div>
        <Button data-testid="new-form-btn" onClick={openCreate} className="bg-[#003366] hover:bg-[#004080] text-white">
          <Plus className="w-4 h-4 mr-2" /> {t('customForms.newTemplate')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            data-testid="search-forms"
            placeholder={t('customForms.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 bg-white"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48 bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.allCategories')}</SelectItem>
            {CATEGORIES.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Templates Grid */}
      {filteredTemplates.length === 0 ? (
        <Card className="bg-white border-gray-100">
          <CardContent className="py-16 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">{t('customForms.noTemplates')}</p>
            <p className="text-sm text-gray-400 mt-1">{t('customForms.createFirst')}</p>
            <Button onClick={openCreate} variant="outline" className="mt-4">
              <Plus className="w-4 h-4 mr-2" /> {t('customForms.newTemplate')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        /* Compact list view (Alian's video — "una listica con doble clic, no cuadros tan grandes") */
        <Card className="bg-white border-gray-100 shadow-sm overflow-hidden">
          <CardContent className="p-0">
            <div className="divide-y divide-gray-100">
              {filteredTemplates.map(template => (
                <div
                  key={template.id}
                  data-testid={`form-template-${template.id}`}
                  onDoubleClick={() => { if (!template.is_structured) openEdit(template); }}
                  draggable={canReorder && !template.is_structured}
                  onDragStart={(e) => handleDragStart(e, template.id)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, template.id)}
                  className={`flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50/50 transition-colors group ${draggedId === template.id ? 'opacity-40' : ''} ${template.is_structured ? '' : 'cursor-pointer'}`}
                  role="button"
                  tabIndex={0}
                >
                  {canReorder && !template.is_structured ? (
                    <GripVertical className="w-4 h-4 text-gray-300 group-hover:text-gray-400 cursor-grab flex-shrink-0" data-testid={`form-drag-handle-${template.id}`} />
                  ) : (
                    <span className="w-4 flex-shrink-0" />
                  )}
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${
                    template.pdf_url ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'
                  }`}>
                    <FileText className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 truncate group-hover:text-[#003366]">{template.name}</span>
                      {template.is_structured && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded font-medium" data-testid={`form-system-badge-${template.id}`}>{t('customForms.systemForm')}</span>
                      )}
                      {template.pdf_url && <span className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-600 rounded font-mono">PDF</span>}
                      {template.official_role && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded font-semibold" data-testid={`form-official-badge-${template.id}`}>
                          OFICIAL — {template.official_role === 'permit_application' ? 'Permit Application' : 'Completion Report'}
                        </span>
                      )}
                      {!template.is_active && <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">{t('common.inactive')}</span>}
                      {(template.applicable_counties || []).length > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-cyan-50 text-cyan-700 rounded">
                          {(template.applicable_counties || []).slice(0, 2).join(', ')}
                          {(template.applicable_counties || []).length > 2 ? '…' : ''}
                        </span>
                      )}
                    </div>
                    {template.description && (
                      <p className="text-xs text-gray-400 truncate mt-0.5">{template.description}</p>
                    )}
                  </div>
                  <span className="text-[11px] text-gray-400 hidden sm:inline-block whitespace-nowrap">
                    {(template.fields?.length || 0)} {t('customForms.fields')}
                  </span>
                  <span className="text-[11px] text-gray-400 hidden md:inline-block whitespace-nowrap">
                    {getCategoryLabel(template.category)}
                  </span>
                  {template.is_structured ? (
                    <div className="w-8 h-8 flex-shrink-0" />
                  ) : (
                  <div className="flex items-center gap-1">
                    <Button
                      data-testid={`form-edit-${template.id}`}
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={(e) => { e.stopPropagation(); openEdit(template); }}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={e => e.stopPropagation()}>
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(template)}>
                          <Pencil className="w-4 h-4 mr-2" /> {t('common.edit')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => duplicateTemplate(template)}>
                          <Copy className="w-4 h-4 mr-2" /> {t('common.duplicate')}
                        </DropdownMenuItem>
                        {template.pdf_url && (
                          <DropdownMenuItem onClick={() => window.open(
                            template.pdf_url.startsWith('http')
                              ? template.pdf_url
                              : `${process.env.REACT_APP_BACKEND_URL}${template.pdf_url}`,
                            '_blank'
                          )}>
                            <Eye className="w-4 h-4 mr-2" /> {t('customForms.viewPdf')}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => handleDelete(template.id)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> {t('common.delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#003366]" />
              {editing ? t('customForms.editTemplate') : t('customForms.newTemplate')}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="bg-gray-100 h-10 flex-shrink-0">
              <TabsTrigger value="details" className="text-xs data-[state=active]:bg-white gap-1.5">
                <FileText className="w-3.5 h-3.5" /> {t('customForms.tabDetails')}
              </TabsTrigger>
              <TabsTrigger value="pdf" className="text-xs data-[state=active]:bg-white gap-1.5">
                <Upload className="w-3.5 h-3.5" /> {t('customForms.tabPdf')}
              </TabsTrigger>
              <TabsTrigger value="fields" className="text-xs data-[state=active]:bg-white gap-1.5">
                <Type className="w-3.5 h-3.5" /> {t('customForms.tabFields')}
              </TabsTrigger>
            </TabsList>
            
            <div className="flex-1 overflow-y-auto mt-4">
              {/* Details Tab */}
              <TabsContent value="details" className="m-0 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>{t('customForms.templateName')} *</Label>
                    <Input
                      data-testid="form-name"
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      placeholder={t('customForms.templateNamePlaceholder')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('customForms.category')}</Label>
                    <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-3 pt-6">
                    <Switch
                      checked={form.is_active}
                      onCheckedChange={checked => setForm({ ...form, is_active: checked })}
                    />
                    <Label>{t('customForms.activeTemplate')}</Label>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Formulario Oficial (con sello del condado)</Label>
                    <Select
                      value={form.official_role || '__none__'}
                      onValueChange={v => setForm({ ...form, official_role: v === '__none__' ? '' : v })}
                    >
                      <SelectTrigger data-testid="form-official-role">
                        <SelectValue placeholder="Ninguno — formulario normal" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Ninguno — formulario normal</SelectItem>
                        <SelectItem value="permit_application">Usar como Permit Application oficial</SelectItem>
                        <SelectItem value="completion_report">Usar como Completion Report oficial</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      Marca este PDF como el formulario ORIGINAL (con sello) que se muestra y rellena en la pestaña Permits — reemplaza el PDF generado internamente. Solo un formulario puede tener cada rol.
                    </p>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>{t('common.description')}</Label>
                    <Textarea
                      value={form.description}
                      onChange={e => setForm({ ...form, description: e.target.value })}
                      placeholder={t('customForms.descriptionPlaceholder')}
                      rows={3}
                    />
                  </div>
                  {/* Per-county scoping (Alian's video — different counties have different forms) */}
                  <div className="col-span-2 space-y-2" data-testid="form-counties-section">
                    <Label className="flex items-center gap-2">
                      <span>{t('customForms.applicableCounties')}</span>
                      {(form.applicable_counties || []).length === 0 && (
                        <span className="text-[11px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded font-medium">
                          {t('customForms.allCounties')}
                        </span>
                      )}
                    </Label>
                    <div className="flex flex-wrap gap-1.5 p-3 bg-gray-50 border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                      {counties.length === 0 ? (
                        <p className="text-xs text-gray-400 italic">No hay condados configurados. Agrega condados en Configuración → Condados.</p>
                      ) : counties.map(cnty => {
                        const selected = (form.applicable_counties || []).includes(cnty);
                        return (
                          <button
                            key={cnty}
                            type="button"
                            data-testid={`county-toggle-${cnty}`}
                            onClick={() => {
                              const cur = form.applicable_counties || [];
                              setForm({
                                ...form,
                                applicable_counties: selected
                                  ? cur.filter(c => c !== cnty)
                                  : [...cur, cnty]
                              });
                            }}
                            className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                              selected
                                ? 'bg-[#003366] text-white border-[#003366]'
                                : 'bg-white text-gray-600 border-gray-300 hover:border-[#003366] hover:text-[#003366]'
                            }`}
                          >
                            {cnty}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-gray-500">{t('customForms.applicableCountiesHint')}</p>
                  </div>
                </div>
              </TabsContent>
              
              {/* PDF Tab */}
              <TabsContent value="pdf" className="m-0">
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-start gap-2">
                    <FileUp className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-700">
                      <strong>¿Cómo funciona?</strong> Sube aquí el PDF oficial (permiso, NOC, lien, etc.). Luego en la pestaña <strong>Campos</strong> posiciona los campos encima del PDF — al llenar el formulario desde un Job o Permiso, se auto-completará con los datos del trabajo.
                    </p>
                  </div>
                  {pdfUploadError && (
                    <div data-testid="pdf-upload-error-banner" className="bg-red-50 border-2 border-red-300 rounded-lg px-4 py-3 flex items-start gap-2">
                      <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-red-700">
                        <p className="font-semibold">{pdfUploadError}</p>
                        <p className="text-xs text-red-600 mt-1">{t('customForms.pdfWordHelp')}</p>
                      </div>
                    </div>
                  )}
                  <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-[#003366]/30 transition-colors">
                    <input
                      type="file"
                      id="pdf-upload"
                      data-testid="pdf-upload-input"
                      accept="application/pdf,.pdf"
                      onChange={handlePdfUpload}
                      className="hidden"
                    />
                    <label htmlFor="pdf-upload" className="cursor-pointer">
                      <FileUp className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-600 font-medium">{t('customForms.uploadPdf')}</p>
                      <p className="text-sm text-gray-400 mt-1">{t('customForms.uploadPdfDesc')}</p>
                    </label>
                  </div>
                  
                  {pdfPreviewUrl && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2 flex items-center justify-between border-b">
                        <span className="text-sm font-medium text-gray-700">{t('customForms.pdfPreview')}</span>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setForm({ ...form, pdf_data: null });
                            setPdfPreviewUrl(null);
                            setPdfUploadError(null);
                          }}
                          className="text-red-500 hover:text-red-600"
                        >
                          <X className="w-4 h-4 mr-1" /> {t('common.remove')}
                        </Button>
                      </div>
                      <div className="h-96 bg-gray-100">
                        <iframe
                          src={pdfPreviewUrl}
                          className="w-full h-full"
                          title="PDF Preview"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
              
              {/* Fields Tab */}
              <TabsContent value="fields" className="m-0">
                {pdfPreviewUrl ? (
                  /* PDF Designer Mode - Visual field placement on PDF */
                  <div className="h-[500px]">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-lg">
                        {t('customForms.pdfDesignerMode')}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setFullscreenDesigner(true)}
                      >
                        <Maximize2 className="w-4 h-4 mr-1" />
                        {t('customForms.fullscreen')}
                      </Button>
                    </div>
                    <Suspense fallback={
                      <div className="flex items-center justify-center h-full">
                        <div className="w-8 h-8 border-3 border-[#003366]/20 border-t-[#003366] rounded-full animate-spin" />
                      </div>
                    }>
                      <FormDesigner
                        pdfUrl={pdfPreviewUrl}
                        fields={form.fields}
                        onFieldsChange={(newFields) => setForm({ ...form, fields: newFields })}
                        selectedField={selectedField}
                        onSelectField={setSelectedField}
                        onAutoMapCuttings={editing?.id ? handleAutoMapCuttings : undefined}
                      />
                    </Suspense>
                  </div>
                ) : (
                  /* Standard Fields Mode - No PDF */
                  <div className="grid grid-cols-3 gap-4 h-[400px]">
                    {/* Field Types Panel */}
                    <div className="space-y-3">
                      <p className="text-sm font-semibold text-gray-700">{t('customForms.addField')}</p>
                      <div className="space-y-2">
                        {FIELD_TYPES.map(fieldType => {
                          const Icon = fieldType.icon;
                          return (
                            <button
                              key={fieldType.type}
                              onClick={() => addField(fieldType.type)}
                              className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-[#003366] hover:bg-[#003366]/5 transition-all text-left"
                            >
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${fieldType.color}`}>
                                <Icon className="w-4 h-4" />
                              </div>
                              <span className="text-sm font-medium text-gray-700">{fieldType.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Fields List */}
                    <div className="col-span-2 border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2 border-b flex items-center justify-between">
                        <span className="text-sm font-semibold text-gray-700">
                          {t('customForms.formFields')} ({form.fields.length})
                        </span>
                      </div>
                      <div className="p-4 space-y-2 overflow-y-auto max-h-[340px]">
                        {form.fields.length === 0 ? (
                          <div className="text-center py-8">
                            <Upload className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                            <p className="text-gray-400">{t('customForms.noFields')}</p>
                            <p className="text-xs text-gray-400 mt-1">{t('customForms.uploadPdfForVisual')}</p>
                          </div>
                        ) : (
                          form.fields.map((field, idx) => {
                            const typeInfo = getFieldTypeInfo(field.type);
                            const Icon = typeInfo.icon;
                            const isMapped = !!field.mapped_field;
                            return (
                              <div
                                key={field.id}
                                className={`flex flex-col gap-2 p-3 rounded-lg border transition-all ${
                                  selectedField === field.id 
                                    ? 'border-[#003366] bg-[#003366]/5' 
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                                onClick={() => setSelectedField(field.id)}
                              >
                                <div className="flex items-center gap-3">
                                  <GripVertical className="w-4 h-4 text-gray-300 cursor-move" />
                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${typeInfo.color}`}>
                                    <Icon className="w-4 h-4" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <Input
                                      value={field.label}
                                      onChange={e => updateField(field.id, { label: e.target.value })}
                                      className="h-8 text-sm"
                                      onClick={e => e.stopPropagation()}
                                      data-testid={`field-label-input-${field.id}`}
                                    />
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <label className="flex items-center gap-1 text-xs text-gray-500">
                                      <input
                                        type="checkbox"
                                        checked={field.required}
                                        onChange={e => updateField(field.id, { required: e.target.checked })}
                                        className="rounded"
                                        onClick={e => e.stopPropagation()}
                                      />
                                      {t('common.required')}
                                    </label>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={(e) => { e.stopPropagation(); removeField(field.id); }}
                                      className="h-7 w-7 text-red-400 hover:text-red-600"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                </div>
                                {/* Auto-fill mapping (Field Value from Related Record) */}
                                {field.type !== 'heading' && field.type !== 'signature' && field.type !== 'image_upload' && (
                                  <div className="flex items-center gap-2 pl-12" onClick={e => e.stopPropagation()}>
                                    <Database className={`w-3.5 h-3.5 flex-shrink-0 ${isMapped ? 'text-emerald-600' : 'text-gray-300'}`} />
                                    <Select
                                      value={field.mapped_field || '__none__'}
                                      onValueChange={v => updateField(field.id, { mapped_field: v === '__none__' ? '' : v })}
                                    >
                                      <SelectTrigger
                                        className={`h-7 text-xs flex-1 ${isMapped ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-white text-gray-500'}`}
                                        data-testid={`field-mapping-select-${field.id}`}
                                      >
                                        <SelectValue placeholder="Auto-fill from… (None — manual entry)" />
                                      </SelectTrigger>
                                      <SelectContent className="max-h-72">
                                        <SelectItem value="__none__">None — manual entry</SelectItem>
                                        {Object.entries(RELATED_FIELD_GROUPS).map(([key, group]) => (
                                          <div key={key}>
                                            <div className="px-2 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide bg-gray-50">
                                              {group.label}
                                            </div>
                                            {group.fields.map(f => (
                                              <SelectItem key={f.value} value={f.value}>
                                                <span className="text-xs">{f.label}</span>
                                              </SelectItem>
                                            ))}
                                          </div>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </TabsContent>
            </div>
          </Tabs>
          
          <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              data-testid="save-form-btn"
              onClick={handleSave}
              className="bg-[#003366] hover:bg-[#004080] text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fullscreen Form Designer Dialog */}
      <Dialog open={fullscreenDesigner} onOpenChange={setFullscreenDesigner}>
        <DialogContent className="max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="font-['Barlow'] text-xl flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#003366]" />
              {t('customForms.formDesigner')} - {form.name || t('customForms.newTemplate')}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            {pdfPreviewUrl && (
              <Suspense fallback={
                <div className="flex items-center justify-center h-full">
                  <div className="w-8 h-8 border-3 border-[#003366]/20 border-t-[#003366] rounded-full animate-spin" />
                </div>
              }>
                <FormDesigner
                  pdfUrl={pdfPreviewUrl}
                  fields={form.fields}
                  onFieldsChange={(newFields) => setForm({ ...form, fields: newFields })}
                  selectedField={selectedField}
                  onSelectField={setSelectedField}
                  onAutoMapCuttings={editing?.id ? handleAutoMapCuttings : undefined}
                />
              </Suspense>
            )}
          </div>
          <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
            <Button variant="outline" onClick={() => setFullscreenDesigner(false)}>
              {t('common.close')}
            </Button>
            <Button
              onClick={() => { handleSave(); setFullscreenDesigner(false); }}
              className="bg-[#003366] hover:bg-[#004080] text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
